/**
 * SEO URL Standardizer
 * Ensures all internal links follow the canonical format (with trailing slash)
 */

const BASE_URL = 'https://courseecho.com';

const ensureTrailingSlash = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname : `${pathname}/`;
};

/**
 * Ensures a path starts with / and ends with /
 * @param path The path to normalize
 * @returns Normalized path
 */
export const normalizePath = (path: string): string => {
  if (!path) return '/';
  
  // Handle external links
  if (path.startsWith('http') || path.startsWith('mailto:') || path.startsWith('tel:')) {
    return path;
  }

  // Handle hash links or queries
  const [urlPath] = path.split(/[?#]/);
  const suffix = path.slice(urlPath.length);

  let normalized = urlPath;
  
  // Ensure starts with /
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }

  // Ensure ends with / (unless it's a file with extension)
  if (!normalized.endsWith('/') && !normalized.includes('.')) {
    normalized += '/';
  }

  return normalized + suffix;
};

/**
 * Normalizes an internal or absolute URL so canonical, OG, and sitemap URLs match exactly.
 */
export const normalizeSeoUrl = (url: string, fallbackPath = '/'): string => {
  if (!url) {
    return `${BASE_URL}${normalizePath(fallbackPath)}`;
  }

  if (url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }

  try {
    const resolved = url.startsWith('http') ? new URL(url) : new URL(url, BASE_URL);
    resolved.protocol = 'https:';
    resolved.pathname = ensureTrailingSlash(resolved.pathname.replace(/\/+/g, '/'));
    return resolved.toString();
  } catch {
    return `${BASE_URL}${normalizePath(url)}`;
  }
};

/**
 * Generates a full canonical URL for a given path
 */
export const getCanonicalUrl = (path: string): string => {
  const normalized = normalizePath(path);
  return `${BASE_URL}${normalized}`;
};
